import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import { MoreVert, Replay, Android, NoteOutlined, ScheduleOutlined } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import CloseTicketDialog from "../CloseTicketDialog";
import TicketNotesDialog from "../TicketNotesDialog";
import ScheduleDialog from "../ScheduleDialog";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "0 1 auto",
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		justifyContent: "flex-end",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
}));

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const [closeOpen, setCloseOpen] = useState(false);
	const [notesOpen, setNotesOpen] = useState(false);
	const [scheduleOpen, setScheduleOpen] = useState(false);
	const [botActive, setBotActive] = useState(!!ticket.botActive);

	useEffect(() => {
		setBotActive(!!ticket.botActive);
	}, [ticket.botActive]);

	const handleToggleBot = async () => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}/bot`, { active: !botActive });
			setBotActive(!botActive);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	};

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId, closeReasonId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
				...(status === "closed" && closeReasonId ? { closeReasonId } : {}),
			});

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleConfirmClose = async closeReasonId => {
		setCloseOpen(false);
		await handleUpdateTicketStatus(null, "closed", user?.id, closeReasonId);
	};

	const botButton = !ticket.isGroup && ticket.status !== "closed" && (
		<Tooltip
			arrow
			title={
				botActive
					? i18n.t("messagesList.header.buttons.botTurnOff")
					: i18n.t("messagesList.header.buttons.botTurnOn")
			}
		>
			<span>
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Android />}
					size="small"
					variant={botActive ? "contained" : "outlined"}
					color={botActive ? "secondary" : "default"}
					onClick={handleToggleBot}
				>
					{botActive
						? i18n.t("messagesList.header.buttons.botOn")
						: i18n.t("messagesList.header.buttons.botOff")}
				</ButtonWithSpinner>
			</span>
		</Tooltip>
	);

	return (
		<div className={classes.actionButtons}>
			{botButton}
			<Tooltip arrow title={i18n.t("quality.notes")}>
				<IconButton size="small" onClick={() => setNotesOpen(true)}>
					<NoteOutlined />
				</IconButton>
			</Tooltip>
			{!ticket.isGroup && (
				<Tooltip arrow title={i18n.t("schedule.title")}>
					<IconButton size="small" onClick={() => setScheduleOpen(true)}>
						<ScheduleOutlined />
					</IconButton>
				</Tooltip>
			)}
			<ScheduleDialog
				open={scheduleOpen}
				onClose={() => setScheduleOpen(false)}
				ticketId={ticket.id}
			/>
			<TicketNotesDialog
				open={notesOpen}
				onClose={() => setNotesOpen(false)}
				ticketId={ticket.id}
			/>
			<CloseTicketDialog
				open={closeOpen}
				busy={loading}
				onClose={() => setCloseOpen(false)}
				onConfirm={handleConfirmClose}
			/>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={() => setCloseOpen(true)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
		</div>
	);
};

export default TicketActionButtons;
